<template>
  <div class="dashboard">
    <SectionHeader
      :title="$t('dashboard.title')"
      :projectSelect="true"
      @selectProjectUuids="
        projectUuids = $event
        getData()
      "
    />

    <div class="body-content">
      <div class="dashboard-wrap">
        <h3 class="dashboard-header">{{ $t('dashboard.routesToday') }}</h3>
        <LoadingSpinner v-if="loading" />
        <DashboardRoutes v-else :routes="routes" />
      </div>

      <div class="row row-gutter-20">
        <div class="col-24">
          <div class="dashboard-wrap">
            <h3 class="dashboard-header">{{ $t('dashboard.reactions') }}</h3>
            <div class="dashboard-select">
              <DateRangePicker
                v-model="dateRange"
                :maxDate="new Date()"
                @update="getReactions"
                opens="right"
                :localeData="localeData"
                :ranges="ranges"
              />
              <span>
                <ToggleButtons v-model="filterUnread" :options="filterUnreadOptions" @input="getReactions" />
              </span>
            </div>
            <label>
              <input
                type="checkbox"
                :checked="reactions && selectedReactionUuids.length === reactions.length"
                @change="toggleSelectAll"
                class="checkbox"
              />
              &nbsp;
              {{ $t('dashboard.selectAll') }}
            </label>
            <button
              @click="markAs({ read: !selectedReactionsAllRead })"
              :disabled="!selectedReactionUuids.length"
              class="button button-green button-small button-round"
            >
              {{
                $tc(
                  selectedReactionsAllRead ? 'pluralize.markAsUnread' : 'pluralize.markAsRead',
                  selectedReactionUuids.length,
                )
              }}
            </button>
            <LoadingSpinner v-if="loading" />
            <template v-else>
              {{
                $tc('pluralize.reactionsMeta', reactionsMeta.count, { from: reactionsMeta.from, to: reactionsMeta.to })
              }}
              <ul class="reaction-list">
                <li v-for="reaction in reactions" :key="reaction.uuid">
                  <Reaction :reaction="reaction" v-model="selectedReactionUuids" />
                </li>
                <li v-if="reactions.length === 0">
                  <p>{{ $t('dashboard.noReactions') }}</p>
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'

import { DATEPICKER_LOCALE_DATA, DATEPICKER_RANGES } from '@/views/config'

import DashboardRoutes from '@/components/DashboardRoutes'
import LoadingSpinner from '@/components/LoadingSpinner'
import Reaction from '@/components/Reaction'
import SectionHeader from '@/components/SectionHeader'
import ToggleButtons from '@/components/ToggleButtons'
import dateUtils from '@/components/mixins/dateUtils'

import routeStopReactionsApi from '@/api/route_stop_reactions'
import routesApi from '@/api/routes'

export default {
  name: 'MainDashboard',
  inject: ['companyUser', 'showPopup'],
  mixins: [dateUtils],
  components: { DateRangePicker, DashboardRoutes, LoadingSpinner, Reaction, SectionHeader, ToggleButtons },
  data: () => ({
    loading: true,
    filterUnread: false,
    projectUuids: [],
    routes: null,
    reactions: null,
    dateRange: {
      startDate: moment().subtract(1, 'month'),
      endDate: moment(),
    },
    selectedReactionUuids: [],
  }),
  computed: {
    reactionsMeta() {
      return {
        count: this.reactions?.length,
        from: this.formattedDate(this.dateRange.startDate),
        to: this.formattedDate(this.dateRange.endDate),
      }
    },
    filterUnreadOptions() {
      return [
        { label: this.$t('dashboard.filterUnreadOptions.all'), value: false },
        { label: this.$t('dashboard.filterUnreadOptions.onlyUnread'), value: true },
      ]
    },
    selectedReactionsAllRead() {
      if (this.reactions == null) return
      return this.reactions
        .filter(({ uuid }) => this.selectedReactionUuids.includes(uuid))
        .every(reaction => !!reaction.notification_read_at)
    },
    localeData() {
      return DATEPICKER_LOCALE_DATA[this.companyUser.user.language]
    },
    ranges() {
      return DATEPICKER_RANGES[this.companyUser.user.language]
    },
  },
  methods: {
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedReactionUuids = this.reactions.map(reaction => reaction.uuid)
      } else {
        this.selectedReactionUuids = []
      }
    },
    async markAs({ read }) {
      const notification_read_at = read ? moment().toISOString() : null

      await routeStopReactionsApi.update({
        company_uuid: this.companyUser.company_uuid,
        route_stop_uuids: this.selectedReactionUuids,
        notification_read_at,
      })

      await this.getReactions()
    },
    async getRoutes() {
      this.routes = await routesApi.getTodays({
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
      })
    },
    async getReactions() {
      this.reactions = await routeStopReactionsApi.get({
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        only_unread: this.filterUnread,
        from: moment(this.dateRange.startDate).startOf('day'),
        to: moment(this.dateRange.endDate).endOf('day'),
      })

      // making sure we don't have any reactions selected that are not actually visible
      this.selectedReactionUuids = this.selectedReactionUuids.filter(uuid =>
        this.reactions.find(reaction => reaction.uuid === uuid),
      )
    },
    async getData() {
      this.loading = true

      await this.getRoutes()
      await this.getReactions()

      this.loading = false
    },
  },
}
</script>

<style lang="scss">
.dashboard-wrap {
  background: #fff;
  -webkit-box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border-radius: var(--border-radius-m);
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: #444;

  a {
    color: #444;
    font-weight: 600;
    box-shadow: 0 1px #ccc;
    line-height: 1.25;
  }
}

.dashboard-header {
  font-size: 2rem;
  margin: 0;
  font-weight: 400;
}

.dashboard-select {
  display: flex;
  gap: 3rem;
  align-items: center;

  > input {
    padding: 0.4rem;
  }
}

.reaction-list {
  list-style: none;
  margin: 0px;
  padding: 0;
  width: 100%;
  align-self: stretch;

  & li {
    width: 100%;
    border-bottom: 1px solid #f1f1f1;
  }
}
</style>
